import {
  ArchiveBoxIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  CodeBracketSquareIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  CursorArrowRaysIcon,
  DocumentCurrencyDollarIcon,
  FaceSmileIcon,
  FilmIcon,
  KeyIcon,
  PaperAirplaneIcon,
  PresentationChartLineIcon,
  PuzzlePieceIcon,
  QueueListIcon,
  RocketLaunchIcon,
  SparklesIcon,
  TrashIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import DataTypes from "~data";
import { Product } from "~data/Product";
import { TeamMembershipAuthStatusData } from "~services/app/endpoints/auth/statusShow/response";

import RobotIcon from "../../../components/icons/sidebar/Robot";
import TiltedEnvelopeIcon from "../../../components/icons/sidebar/TiltedEnvelope";
import { LinkToParam } from "../../../components/ui/Link";
import { productName } from "../../../formatters/billing";
import { PRODUCTS } from "../../../formatters/entities/Plan";
import t from "../../../i18n";
import {
  cobrowseInstallProductVariants,
  cobrowseIntegrationsProducts,
  cobrowseProducts,
  cobrowseVisitorProductVariants,
} from "../../../pages/cobrowsingShared/constants";
import { getRouteOrigin, productPrefixes } from "../../../paths";
import { useApiData } from "../../../use/api";

export type SidebarItem = {
  count?: number;
  countColor?: "red";
  current: boolean;
  icon: React.ComponentType<any>;
  id:
    | "co.dashboard"
    | "hq.accounts"
    | "hq.activityLog"
    | "hq.aiChanges"
    | "hq.customerSuccess"
    | "hq.dashboards"
    | "hq.globalActivityLog"
    | "hq.root"
    | "hq.sales.leads"
    | "hq.sales.opportunities"
    | "hq.sales.quotes"
    | "hq.sales.tasks"
    | "hs.cobrowse"
    | "ld.create"
    | "ld.leads"
    | "ld.links"
    | "ld.templates"
    | "up.activityLog"
    | "up.billing"
    | "up.getting-started"
    | "up.history"
    | "up.install"
    | "up.integrations"
    | "up.reporting.usage"
    | "up.settings"
    | "up.team"
    | "up.visitorData"
    | "uv.cobrowse"
    | `${(typeof productPrefixes)[keyof typeof productPrefixes]}.root`;
  name: string;
  to: LinkToParam;
};

type SecondarySidebarItem = {
  current: boolean;
  id: string;
  name: string;
  to: LinkToParam;
};

export function getCurrentProduct({
  currentTeamId,
  hostName,
  teams,
}: {
  currentTeamId?: `t_${string}`;
  hostName: string;
  teams: Pick<DataTypes["Teams"], "id" | "onboardingCompleted" | "product">[];
}): Product | undefined {
  const currentTeam = currentTeamId ? teams.find((t) => t.id === currentTeamId)! : undefined,
    onlyProduct = new Set(teams.map((t) => t.product)).size === 1 ? teams[0]!.product : undefined;

  if (hostName.includes("hq.upscope")) return;

  if (hostName.includes("cobrowsingapi")) {
    if ((currentTeam?.product ?? onlyProduct) === "cobrowsingapi") return "cobrowsingapi";
    if ((currentTeam?.product ?? onlyProduct) === "helloscreen") return "helloscreen";
    if ((currentTeam?.product ?? onlyProduct) === "userview") return "userview";
    return "cobrowsingapi";
  }

  if (hostName.includes("helloscreen")) return "helloscreen";

  if (hostName.includes("userview")) return "userview";

  if (hostName.includes("livedocument")) return "livedocument";

  return currentTeam?.product ?? onlyProduct;
}

export function useHqNaviation({
  adminRoles,
  currentRoute,
}: {
  adminRoles: Exclude<DataTypes["Users"]["adminRoles"], null>;
  currentRoute?: SidebarItem["id"];
}): SidebarItem[] {
  const inHq = location.origin === getRouteOrigin("hq.root");
  const { due: tasksDue, total: tasksTotal } = useApiData("hq.sales.tasks.count", undefined, { enable: inHq });
  const { companies: leads } = useApiData(
    "hq.sales.leads.list",
    { everyone: !adminRoles.includes("sales_lead_target") },
    {
      enable: inHq && adminRoles.includes("sales_view"),
    },
  );
  const { opportunities } = useApiData("hq.sales.opportunities.index", undefined, {
    enable: inHq && adminRoles.includes("sales_view"),
  });

  if (adminRoles.length === 0 || !inHq) return [];

  const navigationItems: Array<false | Omit<SidebarItem, "current"> | undefined> = [
    {
      icon: RocketLaunchIcon,
      id: "hq.root",
      name: t("Mission Control"),
      to: ["hq.root"] as LinkToParam,
    },
    {
      icon: FaceSmileIcon,
      id: "hq.customerSuccess",
      name: t("Customer Success"),
      to: ["hq.customerSuccess.index"],
    },
    {
      count: opportunities?.filter((o) => o.followUpNeeded).length || undefined, // eslint-disable-line @typescript-eslint/strict-boolean-expressions
      icon: CurrencyDollarIcon,
      id: "hq.sales.opportunities",
      name: t("Opportunities"),
      to: ["hq.sales.index"],
    },
    {
      icon: DocumentCurrencyDollarIcon,
      id: "hq.sales.quotes",
      name: t("Quotes"),
      to: ["hq.quotes.index"],
    },
    {
      count: leads?.length || undefined, // eslint-disable-line @typescript-eslint/strict-boolean-expressions
      icon: QueueListIcon,
      id: "hq.sales.leads",
      name: t("New Leads"),
      to: ["hq.sales.leads"],
    },
    {
      count: tasksDue || tasksTotal || undefined, // eslint-disable-line @typescript-eslint/strict-boolean-expressions
      countColor: tasksDue ? "red" : undefined, // eslint-disable-line @typescript-eslint/strict-boolean-expressions
      icon: CheckCircleIcon,
      id: "hq.sales.tasks",
      name: t("Tasks"),
      to: ["hq.sales.tasks"],
    },
    {
      icon: ClipboardDocumentListIcon,
      id: "hq.accounts",
      name: t("Accounts"),
      to: ["hq.accounts.index", {}],
    },
    {
      icon: RobotIcon,
      id: "hq.aiChanges",
      name: t("AI Changes"),
      to: ["hq.aiChanges"],
    },
    {
      icon: PresentationChartLineIcon,
      id: "hq.dashboards",
      name: t("Dashboards"),
      to: "https://metabase.upscope.com",
    },
    {
      icon: ArchiveBoxIcon,
      id: "hq.globalActivityLog",
      name: t("Global Activity Log"),
      to: ["hq.globalActivityLog", {}],
    },
  ];

  return navigationItems.filter(Boolean).map((item) => {
    const sidebarItem = item as SidebarItem;
    sidebarItem.current = item.id === currentRoute;
    return sidebarItem;
  });
}

export function usePrimaryNavigation({
  currentProduct,
  currentRoute,
  currentTeamId,
  isAdmin,
  teamMemberships,
}: {
  currentProduct?: Product;
  currentRoute?: SidebarItem["id"];
  currentTeamId?: DataTypes["Teams"]["id"];
  isAdmin: boolean;
  teamMemberships: Array<
    TeamMembershipAuthStatusData & {
      team: Pick<
        DataTypes["Teams"],
        | "agentCanSeeOwnHistory"
        | "delinquentSince"
        | "domain"
        | "featuresAvailable"
        | "id"
        | "onboardingCompleted"
        | "product"
        | "productVariant"
        | "uniqueIdentifier"
      >;
    }
  >;
}): SidebarItem[] {
  if (!currentProduct) {
    return [
      isAdmin && {
        current: false,
        icon: RocketLaunchIcon,
        id: "hq.root",
        name: t("Mission Control"),
        to: ["hq.root"] as LinkToParam,
      },
      ...PRODUCTS.map(
        (product) =>
          teamMemberships.some((membership) => membership.team.product === product) && {
            current: false,
            id: `${productPrefixes[product]}.root`,
            name: productName(product),
            to: [`${productPrefixes[product]}.root`, {}] as LinkToParam,
          },
      ),
    ].filter(Boolean);
  }

  const navTeam =
    teamMemberships.find((membership) => membership.team.id === currentTeamId)?.team || teamMemberships[0]!.team;

  // Filter out teams for other products
  teamMemberships = teamMemberships.filter((membership) => membership.team.product === currentProduct);

  const teams = teamMemberships.map((membership) => membership.team);

  const teamWhere = (
    permission: keyof TeamMembershipAuthStatusData["permissions"],
    noOnboardingTeams?: boolean,
  ): (typeof teamMemberships)[number]["team"] | undefined => {
    let teams = teamMemberships.filter((m) => m.permissions[permission]).map((m) => m.team);
    if (noOnboardingTeams) teams = teams.filter((team) => team.onboardingCompleted);
    return teams.find((t) => t.id === navTeam.id) || teams[0];
  };

  const gettingStartedTeam = teamMemberships.filter((m) => !m.team.onboardingCompleted && m.isOwner)[0]?.team,
    canUseTeam = teamWhere("canUse"),
    canAccessSettingsTeam = teamWhere("canAccessSettings"),
    canManageUsersTeam = teamWhere("canManageUsers"),
    canManageBillingTeam = teamWhere("canManageBilling"),
    canManageContentTeam = teamWhere("canManageContent"),
    canViewReportingTeam = teamWhere("canViewReporting");

  const navigationItems: Array<false | Omit<SidebarItem, "current"> | undefined> = [
    isAdmin && {
      icon: RocketLaunchIcon,
      id: "hq.root",
      name: t("Mission Control"),
      to: ["hq.root"] as LinkToParam,
    },

    gettingStartedTeam && {
      icon: SparklesIcon,
      id: "up.getting-started",
      name: t("Getting started"),
      to: [
        "up.onboarding",
        {
          teamDomain: gettingStartedTeam.domain,
        },
      ] as LinkToParam,
    },

    currentProduct === "livedocument" && {
      icon: PaperAirplaneIcon,
      id: "ld.create",
      name: t("New LiveDocument"),
      to: ["ld.links.create", { teamDomain: navTeam.domain }] as LinkToParam,
    },

    currentProduct === "livedocument" && {
      icon: QueueListIcon,
      id: "ld.links",
      name: t("Links"),
      to: ["ld.links.list", { teamDomain: navTeam.domain }] as LinkToParam,
    },

    currentProduct === "livedocument" && {
      icon: TiltedEnvelopeIcon,
      id: "ld.leads",
      name: t("Leads"),
      to: ["ld.leads.index", { teamDomain: navTeam.domain }] as LinkToParam,
    },

    currentProduct === "livedocument" &&
      canManageContentTeam && {
        icon: ClipboardDocumentIcon,
        id: "ld.templates",
        name: t("Templates"),
        to: ["ld.templates.index", { teamDomain: navTeam.domain }] as LinkToParam,
      },

    currentProduct === "helloscreen" &&
      teams.some((t) => t.productVariant === "helloscreen_cobrowsing") &&
      canUseTeam && {
        icon: CursorArrowRaysIcon,
        id: "hs.cobrowse",
        name: t("Co-browse"),
        to: ["hs.visitorsList", {}] as LinkToParam,
      },

    currentProduct === "userview" &&
      canUseTeam && {
        icon: CursorArrowRaysIcon,
        id: "uv.cobrowse",
        name: t("Co-browse"),
        to: ["uv.visitorsList", {}] as LinkToParam,
      },

    (currentRoute !== "up.getting-started" || teams.some((team) => team.onboardingCompleted)) &&
      cobrowseInstallProductVariants.some((variant) =>
        teams.some((team) => team.product === variant || team.productVariant === variant),
      ) &&
      canAccessSettingsTeam && {
        icon: CodeBracketSquareIcon,
        id: "up.install",
        name: t("Install"),
        to: [
          "up.onboarding.installCobrowsing",
          {
            teamDomain: canAccessSettingsTeam.domain,
            teamUniqueIdentifier: canAccessSettingsTeam.uniqueIdentifier,
          },
        ] as LinkToParam,
      },

    canAccessSettingsTeam &&
      teams.some((team) => team.featuresAvailable.includes("cobrowse_rest_api")) && {
        icon: KeyIcon,
        id: "co.dashboard",
        name: t("REST API"),
        to: ["co.dashboard", { teamDomain: canAccessSettingsTeam.domain }],
      },

    canAccessSettingsTeam &&
      cobrowseIntegrationsProducts.includes(currentProduct) && {
        icon: PuzzlePieceIcon,
        id: "up.integrations",
        name: t("Integrations"),
        to: ["up.integrations", { teamDomain: canAccessSettingsTeam.domain }],
      },

    cobrowseProducts.includes(currentProduct) &&
      (canViewReportingTeam ||
        teams.some(
          (team) =>
            team.featuresAvailable.includes("cobrowse_session_history_own") && team.agentCanSeeOwnHistory !== "deny",
        )) && {
        icon: FilmIcon,
        id: "up.history",
        name: t("Co-browsing History"),
        to: [
          "up.history",
          {
            teamDomain: (canViewReportingTeam ||
              teams.find(
                (team) =>
                  team.featuresAvailable.includes("cobrowse_session_history_own") &&
                  team.agentCanSeeOwnHistory !== "deny",
              ))!.domain,
          },
        ],
      },

    canViewReportingTeam && {
      icon: ArchiveBoxIcon,
      id: "up.activityLog",
      name: t("Activity Log"),
      to: ["up.activity.activityLog", { teamDomain: canViewReportingTeam.domain }],
    },

    canManageUsersTeam && {
      icon: UsersIcon,
      id: "up.team",
      name: t("Team"),
      to: ["up.settings.team", { teamDomain: canManageUsersTeam.domain }],
    },

    canViewReportingTeam &&
      cobrowseProducts.includes(currentProduct) && {
        icon: ChartBarSquareIcon,
        id: "up.reporting.usage",
        name: t("Usage"),
        to: ["up.reporting.usage", { teamDomain: canViewReportingTeam.domain }],
      },

    canAccessSettingsTeam && {
      icon: Cog6ToothIcon,
      id: "up.settings",
      name: t("Settings"),
      to: ["up.settings.general", { teamDomain: canAccessSettingsTeam.domain }],
    },

    canManageBillingTeam && {
      count: canManageBillingTeam.delinquentSince ? 1 : undefined,
      countColor: "red",
      icon: CreditCardIcon,
      id: "up.billing",
      name: t("Billing"),
      to: ["up.settings.billing", { teamDomain: canManageBillingTeam.domain }],
    },

    canManageContentTeam &&
      cobrowseVisitorProductVariants.some((variant) =>
        teams.some((team) => team.product === variant || team.productVariant === variant),
      ) && {
        icon: TrashIcon,
        id: "up.visitorData",
        name: t("Delete Visitor Data"),
        to: ["up.visitorData", { teamDomain: canManageContentTeam.domain }],
      },
  ];

  return navigationItems.filter(Boolean).map((item) => {
    const sidebarItem = item as SidebarItem;
    sidebarItem.current = item.id === currentRoute;
    return sidebarItem;
  });
}

export function useSecondaryNavigation({
  currentProduct,
  currentTeamId,
  primaryNavigation,
  teams,
}: {
  currentProduct?: Product;
  currentTeamId?: DataTypes["Teams"]["id"];
  primaryNavigation: SecondarySidebarItem[];
  teams: Array<Pick<DataTypes["Teams"], "domain" | "id" | "onboardingCompleted" | "product" | "uniqueIdentifier">>;
}): SecondarySidebarItem[] | undefined {
  if (!currentProduct) return;
  const currentNavigation = primaryNavigation.find((item) => item.current);
  if (
    typeof currentNavigation?.to[1] === "undefined" || // It's not a dynamic route
    !Array.isArray(currentNavigation.to) ||
    !("teamDomain" in currentNavigation.to[1])
  )
    return;

  // Filter out teams that don't have the current product
  let possibleTeams = teams.filter((team) => team.product === currentProduct);

  if (currentNavigation.id === "up.getting-started")
    possibleTeams = possibleTeams.filter((team) => !team.onboardingCompleted);

  if (possibleTeams.length < 2) return;

  return possibleTeams.map((team) => {
    const sidebarItem: SecondarySidebarItem = {
      current: team.id === currentTeamId,
      id: team.id,
      name: team.domain,
      to: [
        currentNavigation.to[0],
        {
          teamDomain: team.domain,
          teamUniqueIdentifier: team.uniqueIdentifier,
        },
      ] as LinkToParam,
    };
    return sidebarItem;
  });
}
